import { Box, Typography, useMediaQuery } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Visibility } from '@mui/icons-material';
import { IBuild, IResponse, ISortDirection } from '../../../models';
import { useQuery } from 'react-query';
import { getSalesBuilds } from '../../../fetch/lookups';
import { Button, MobileTable, Pagination, Table } from '../../../components';
import {
  SALES_PORTAL_PER_PAGE_OPTIONS,
  formatDate,
  formatPascalStringToSpaces,
  getEnvironment
} from '../../../helpers';
import { TenDayBuildsFilters } from './ten-day-builds-filters';
import { SalesContext, update } from '../../../context/sales';
import { theme } from '../../../styles/theme';

const PAGE_NAME = 'ten-day-builds';
export const TenDayBuildsList = () => {
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const removeStickyHeader = useMediaQuery('(max-width: 1246px)');

  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    lotNumber?: number | string;
    planId?: number | string;
    tenDaySelectionsCompleted?: boolean;
  } | null>({});
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');
  const [selectedLotId, setSelectedLotId] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [isTenDayComplete, setIsTenDayComplete] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);

  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);

  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPageContext = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const [selectedSort, setSelectedSort] = useState<string>('NeighborhoodName');
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodName?: ISortDirection;
    LotNumber?: ISortDirection;
    PlanDescription?: ISortDirection;
    JobType?: ISortDirection;
    IsTenDayComplete?: ISortDirection;
    OptionLockoutDate?: ISortDirection;
    Buyer?: ISortDirection;
  }>({
    NeighborhoodName: 'Asc'
  });

  const { isLoading, data: buildsData } = useQuery<IResponse<IBuild[]>, Error>(
    ['getSalesBuilds', page, perPageContext, selectedSort, sortDirection, filters],
    () =>
      getSalesBuilds({
        page: page + 1,
        perPage: perPageContext,
        neighborhoodId: selectedNeighborhoodId,
        lotNumber: selectedLotId,
        planId: selectedPlanId,
        status: 'ContractAccepted',
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...filters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const handleView = (original: IBuild) => {
    history.push(
      `/sales/contracting/ten-day-builds/${original.buildId}?redirect=/sales/contracting/ten-day-builds`
    );
  };

  const data = useMemo(() => buildsData?.records ?? [], [buildsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      !isMobile && {
        Header: 'Neighborhood',
        accessor: 'neighborhood',
        isServerSorted: selectedSort === 'NeighborhoodName',
        isServerSortedDesc: sortDirection.NeighborhoodName === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodName')
      },
      !isMobile && {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'planDescription',
        isServerSorted: selectedSort === 'planDescription',
        isServerSortedDesc: sortDirection.PlanDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('PlanDescription')
      },
      {
        Header: 'Job Type',
        accessor: 'jobType',
        isServerSorted: selectedSort === 'jobType',
        isServerSortedDesc: sortDirection.JobType === 'Desc',
        handleClickColumn: () => handleClickColumn('JobType')
      },
      {
        Header: 'Selections Complete',
        accessor: 'isTenDayComplete',
        isServerSorted: selectedSort === 'isTenDayComplete',
        isServerSortedDesc: sortDirection.IsTenDayComplete === 'Desc',
        handleClickColumn: () => handleClickColumn('IsTenDayComplete'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return <>{original.isTenDayComplete ? 'True' : 'False'}</>;
        }
      },
      {
        Header: 'Lockout Date',
        accessor: 'optionLockoutDate',
        isServerSorted: selectedSort === 'optionLockoutDate',
        isServerSortedDesc: sortDirection.OptionLockoutDate === 'Desc',
        handleClickColumn: () => handleClickColumn('OptionLockoutDate'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return <>{original.optionLockoutDate ? formatDate(original.optionLockoutDate) : null}</>;
        }
      },
      !isMobile && {
        Header: 'Contact Name',
        accessor: 'buyer',
        isServerSorted: selectedSort === 'buyer',
        isServerSortedDesc: sortDirection.Buyer === 'Desc',
        handleClickColumn: () => handleClickColumn('Buyer')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IBuild } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id={`view-build-${original.buildId}`}
                type="button"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => handleView(original)}
              >
                Details
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, isMobile]);

  return (
    <>
      <TenDayBuildsFilters
        isLoading={isLoading}
        selectedNeighborhoodId={selectedNeighborhoodId}
        selectedLotId={selectedLotId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodId}
        setSelectedLotId={setSelectedLotId}
        selectedPlanId={selectedPlanId}
        setSelectedPlanId={setSelectedPlanId}
        isTenDayComplete={isTenDayComplete}
        setIsTenDayComplete={setIsTenDayComplete}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              neighborhoodId: '',
              lotNumber: '',
              planId: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              neighborhoodId: selectedNeighborhoodId || undefined,
              lotNumber: selectedLotId || undefined,
              planId: selectedPlanId || undefined,
              tenDaySelectionsCompleted: isTenDayComplete || undefined
            });
          }
        }}
      />
      <Table
        stickyHeaders={removeStickyHeader ? false : true}
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          getAccordionHeader: (original: IBuild) => {
            let neighborhood = original.neighborhood;
            let phase = original.phase;
            let lot = original.lotNumber;
            let buyer = original.buyer;
            let status = original.status;
            return (
              <>
                {neighborhood} - Phase {phase} - Lot {lot}
                {buyer && (
                  <>
                    <br />
                    <Typography color={theme.palette.grey[800]} variant="caption">
                      {buyer}
                    </Typography>
                  </>
                )}
                {status && (
                  <Box>
                    <Typography
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
                        color: theme.palette.common.white,
                        borderRadius: theme.spacing(0.25)
                      }}
                      variant="caption"
                    >
                      {formatPascalStringToSpaces(status)}
                    </Typography>
                  </Box>
                )}
              </>
            );
          },
          handleEdit: (val: IBuild) => handleView(val)
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPageContext}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
    </>
  );
};
